import { useEffect, useMemo, useState } from "react";
import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Checkmark16Regular, ChevronLeft16Regular, ChevronRight16Regular, Info16Regular, Warning16Regular } from "@fluentui/react-icons";
import { AppDialog, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { todayISO } from "@/engine/date";
import { computeEvm, topDelays } from "@/engine/health";
import type { Project } from "@/engine/types";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { fmtDelta, fmtSpi, modKey } from "@/lib/format";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { useProjectStore } from "@/store/project-store";
import * as ops from "@/store/tree-ops";

type Step = "date" | "tasks" | "summary";
interface Edit {
  percentComplete?: number;
  duration?: number;
}

const PCT_PRESETS = [0, 25, 50, 75, 100] as const;

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  presetGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    width: "fit-content",
    overflow: "hidden",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  presetBtn: {
    minWidth: "52px",
    height: "24px",
    borderRadius: "0px",
    border: "none",
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: "none",
  },
  presetLast: { borderRight: "none" },
  metrics3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
  },
  metricValue: { fontVariantNumeric: "tabular-nums", fontSize: tokens.fontSizeBase300 },
  metricBad: { color: tokens.colorPaletteRedForeground1 },
  callout: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  banner: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    alignItems: "flex-start",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  bannerWarn: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  bannerInfo: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  bannerOk: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
  },
  meta: { color: tokens.colorNeutralForeground3 },
  hint: { color: tokens.colorNeutralForeground3 },
  delayRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  delayName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  fields: {
    display: "flex",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
});

/**
 * Weekly status update in three steps: pick the status date → walk through every task that should
 * have started by then → review the impact (SPI, finish, top delays) and apply as one undoable step.
 */
export function StatusUpdateDialog() {
  const open = useProjectStore((s) => s.dialog === "status-update");
  const close = () => useProjectStore.getState().openDialog(null);
  return <Wizard open={open} onClose={close} />;
}

function Wizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const styles = useStyles();
  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const own = useOwnWork();
  const canEditDuration = usePlanSurface().editSchedule;
  const [step, setStep] = useState<Step>("date");
  const [statusDate, setStatusDate] = useState(() => (todayISO() > project.statusDate ? todayISO() : project.statusDate));
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!open) return;
    setStep("date");
    setEdits({});
    setI(0);
    setStatusDate(todayISO() > project.statusDate ? todayISO() : project.statusDate);
  }, [open, project.statusDate]);

  const due = useMemo(
    () =>
      derived.schedule.order
        .map((id) => derived.schedule.tasks.get(id)!)
        .filter((t) => !t.isSummary && t.start <= statusDate && t.percentComplete < 100)
        .filter((t) => {
          const task = project.tasks.find((item) => item.id === t.id);
          return !!task && own.scheduleOwn(task);
        })
        .map((t) => t.id),
    [derived, statusDate, project.tasks, own],
  );

  const preview = useMemo(() => {
    if (step !== "summary") return null;
    const p: Project = structuredClone(project);
    p.statusDate = statusDate;
    for (const [id, e] of Object.entries(edits)) p.tasks = ops.updateTask(p.tasks, id, e);
    const cal = new WorkCalendar(p.calendar);
    const schedule = computeSchedule(p, cal);
    const baseline = p.baselines.find((b) => b.id === p.activeBaselineId) ?? p.baselines.at(-1) ?? null;
    return { schedule, evm: computeEvm(p, schedule, baseline, cal), delays: topDelays(p, schedule, baseline, 3, cal) };
  }, [step, project, statusDate, edits]);

  const apply = () => {
    const s = useProjectStore.getState();
    const patches = Object.entries(edits).map(([id, patch]) => ({ id, patch }));
    s.commit(
      `진척 업데이트 (${formatDate(statusDate, "numeric")}) · ${patches.length}개 작업`,
      (p) => {
        let tasks = p.tasks;
        for (const { id, patch } of patches) tasks = ops.updateTask(tasks, id, patch);
        return { ...p, tasks, statusDate };
      },
      { taskIds: patches.map((x) => x.id) },
    );
    onClose();
  };

  if (step === "date") {
    return (
      <AppDialog
        open={open}
        onClose={onClose}
        title="진척 업데이트"
        description="작업별 진척률을 확인할 날짜를 선택하세요"
        footer={
          <>
            <FuiButton density="dialog" appearance="secondary" onClick={onClose}>
              취소
            </FuiButton>
            <FuiButton
              density="dialog"
              appearance="primary"
              onClick={() => {
                setI(0);
                setStep(due.length ? "tasks" : "summary");
              }}
            >
              계속
            </FuiButton>
          </>
        }
      >
        <div className={styles.stack}>
          <DialogGrid2>
            <DialogField label="진척 기준일">
              <DateField className="w-[11.25rem]" value={statusDate} onChange={(v) => v && setStatusDate(v)} size="sm" aria-label="진척 기준일" />
            </DialogField>
            <DialogField label="점검 대상">
              <span className={styles.metricValue}>{due.length}개</span>
            </DialogField>
          </DialogGrid2>
          <div className={styles.callout}>
            <Caption1>
              {own.restricted && !own.self.name
                ? "내 배정을 고르면 그 작업만 나옵니다."
                : due.length === 0
                  ? own.restricted
                    ? "이 배정에 진척을 갱신할 작업이 없습니다."
                    : "진척 기준일까지 시작 예정인 미완료 작업이 없습니다."
                  : `진척 기준일까지 시작 예정인 미완료 작업 ${due.length}개의 진척률을 순서대로 확인합니다.`}
            </Caption1>
          </div>
          {!derived.baseline && (
            <div className={mergeClasses(styles.banner, styles.bannerWarn)}>
              <Warning16Regular style={{ flexShrink: 0, marginTop: 1 }} />
              <span>계획 기준선이 없어 기준선 대비 지연 일수는 계산되지 않습니다.</span>
            </div>
          )}
        </div>
      </AppDialog>
    );
  }

  if (step === "tasks") {
    const id = due[i]!;
    const task = project.tasks.find((t) => t.id === id)!;
    const sched = derived.schedule.tasks.get(id)!;
    const bl = derived.baseline?.tasks[id];
    const edit = edits[id] ?? {};
    const pct = edit.percentComplete ?? task.percentComplete;
    const dur = edit.duration ?? task.duration;
    const setEdit = (e: Edit) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...e } }));
    const next = () => (i < due.length - 1 ? setI(i + 1) : setStep("summary"));
    const prev = () => (i > 0 ? setI(i - 1) : setStep("date"));
    const status = pct >= 100 ? "완료" : sched.finish < statusDate ? "지연" : "진행";
    const span = sched.isMilestone ? "마일스톤" : `${sched.duration}일`;
    return (
      <AppDialog
        open={open}
        onClose={onClose}
        title={task.name || "이름 없는 작업"}
        description={`${i + 1} / ${due.length}개 작업`}
        footerStart={
          <FuiButton density="dialog" appearance="subtle" icon={<ChevronLeft16Regular />} onClick={prev}>
            이전
          </FuiButton>
        }
        footer={
          <>
            <FuiButton density="dialog" appearance="subtle" onClick={next}>
              건너뛰기
            </FuiButton>
            <FuiButton density="dialog" appearance="primary" icon={<ChevronRight16Regular />} iconPosition="after" onClick={next} title="Return으로 다음">
              다음
            </FuiButton>
          </>
        }
      >
        <TaskStep
          key={id}
          pct={pct}
          dur={dur}
          isMilestone={sched.isMilestone}
          status={status}
          meta={`WBS ${sched.wbs} · ${span} · ${formatDateRange(sched.start, sched.finish, "numeric")}${bl && bl.finish !== sched.finish ? ` · 기준 ${formatDate(bl.finish, "numeric")}` : ""}`}
          onPct={(v) => setEdit({ percentComplete: v })}
          onDur={(v) => setEdit({ duration: v })}
          onEnter={next}
          canEditDuration={canEditDuration}
        />
      </AppDialog>
    );
  }

  const before = derived;
  const after = preview!;
  const changed = Object.keys(edits).length;
  const finishDelta =
    new WorkCalendar(project.calendar).ordinalBeforeISO(after.schedule.projectFinish) -
    new WorkCalendar(project.calendar).ordinalBeforeISO(before.schedule.projectFinish);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="진척 업데이트"
      description={`${formatDate(statusDate, "full")}을 진척 기준일로 적용합니다`}
      footerStart={
        <FuiButton
          density="dialog"
          appearance="subtle"
          icon={<ChevronLeft16Regular />}
          onClick={() => (due.length ? (setI(due.length - 1), setStep("tasks")) : setStep("date"))}
        >
          이전
        </FuiButton>
      }
      footer={
        <>
          <FuiButton density="dialog" appearance="secondary" onClick={onClose}>
            취소
          </FuiButton>
          <FuiButton density="dialog" appearance="primary" onClick={apply}>
            업데이트 적용
          </FuiButton>
        </>
      }
    >
      <div className={styles.stack}>
        <Caption1 className={styles.hint}>
          {changed === 0 ? "바뀌는 작업 없음" : `${changed}개 작업이 바뀝니다`} · {modKey}Z로 되돌릴 수 있습니다
        </Caption1>
        <div className={styles.metrics3}>
          <Metric label="일정 진행률 (SPI)" before={fmtSpi(before.evm.spi)} after={fmtSpi(after.evm.spi)} bad={after.evm.spi !== null && after.evm.spi < 0.95} />
          <Metric
            label="종료일"
            before={formatDate(before.schedule.projectFinish, "numeric")}
            after={formatDate(after.schedule.projectFinish, "numeric")}
            delta={finishDelta ? fmtDelta(finishDelta) : undefined}
            bad={finishDelta > 0}
          />
          <Metric label="여유 없는 작업" before={String(before.schedule.criticalPath.length)} after={String(after.schedule.criticalPath.length)} />
        </div>
        {after.delays.length > 0 && (
          <div className={styles.stack}>
            <Caption1 className={styles.hint}>종료일이 밀린 작업</Caption1>
            {after.delays.map((d) => (
              <div key={d.taskId} className={styles.delayRow}>
                <Caption1 className={styles.delayName}>
                  {d.wbs} {d.name}
                </Caption1>
                <Caption1 className={d.critical ? styles.metricBad : undefined}>{fmtDelta(d.varianceDays)}</Caption1>
              </div>
            ))}
          </div>
        )}
        {!derived.baseline && <Caption1 className={styles.hint}>기준선이 없어 진행률과 늦어진 작업은 비어 있습니다.</Caption1>}
      </div>
    </AppDialog>
  );
}

function TaskStep({
  pct,
  dur,
  isMilestone,
  status,
  meta,
  onPct,
  onDur,
  onEnter,
  canEditDuration,
}: {
  pct: number;
  dur: number;
  isMilestone: boolean;
  status: "지연" | "진행" | "완료";
  meta: string;
  onPct: (v: number) => void;
  onDur: (v: number) => void;
  onEnter: () => void;
  canEditDuration: boolean;
}) {
  const styles = useStyles();
  const [pctText, setPctText] = useState(String(pct));
  const [durText, setDurText] = useState(String(dur));
  useEffect(() => setPctText(String(pct)), [pct]);
  useEffect(() => setDurText(String(dur)), [dur]);
  const commitPct = () => onPct(Math.max(0, Math.min(100, Math.round(Number(pctText) || 0))));
  const commitDur = () => onDur(Math.max(1, Math.round(Number(durText) || 1)));
  return (
    <div className={styles.stack}>
      <Caption1 className={styles.meta}>{meta}</Caption1>
      <DialogField label="진척">
        <div className={styles.fields}>
          <div className={styles.presetGroup} role="group" aria-label="진척 프리셋">
            {PCT_PRESETS.map((v, idx, all) => (
              <FuiButton
                key={v}
                density="dialog"
                className={mergeClasses(styles.presetBtn, idx === all.length - 1 && styles.presetLast)}
                appearance={pct === v ? "primary" : "subtle"}
                onClick={() => onPct(v)}
              >
                {v}%
              </FuiButton>
            ))}
          </div>
          <FuiInput
            density="dialog"
            style={{ width: 88 }}
            value={pctText}
            inputMode="numeric"
            contentAfter="%"
            aria-label="진척 %"
            onChange={(_, d) => setPctText(d.value)}
            onBlur={commitPct}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitPct();
                onEnter();
              }
            }}
          />
        </div>
      </DialogField>
      {!isMilestone && canEditDuration && (
        <DialogField label="기간">
          <FuiInput
            density="dialog"
            style={{ width: 88 }}
            value={durText}
            inputMode="numeric"
            contentAfter="일"
            aria-label="기간 일"
            onChange={(_, d) => setDurText(d.value)}
            onBlur={commitDur}
            onKeyDown={(e) => e.key === "Enter" && commitDur()}
          />
        </DialogField>
      )}
      <StatusBanner status={status} canEditDuration={canEditDuration} />
    </div>
  );
}

function StatusBanner({ status, canEditDuration }: { status: "지연" | "진행" | "완료"; canEditDuration: boolean }) {
  const styles = useStyles();
  const Icon = status === "지연" ? Warning16Regular : status === "완료" ? Checkmark16Regular : Info16Regular;
  const tone = status === "지연" ? styles.bannerWarn : status === "완료" ? styles.bannerOk : styles.bannerInfo;
  const text =
    status === "지연"
      ? canEditDuration
        ? "이미 끝났어야 하는 작업입니다. 아직 진행 중이면 기간을 늘리세요."
        : "이미 끝났어야 하는 작업입니다. 진척만 갱신할 수 있습니다."
      : status === "완료"
        ? "작업이 100% 완료되었습니다."
        : "예정된 일정 내에서 정상 진행 중인 작업입니다.";
  return (
    <div className={mergeClasses(styles.banner, tone)}>
      <Icon style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{text}</span>
    </div>
  );
}

function Metric({ label, before, after, delta, bad }: { label: string; before: string; after: string; delta?: string; bad?: boolean }) {
  const styles = useStyles();
  const same = before === after;
  return (
    <div className={styles.metric}>
      <Caption1>{label}</Caption1>
      <span className={mergeClasses(styles.metricValue, bad && styles.metricBad)}>
        {after}
        {delta ? ` ${delta}` : ""}
      </span>
      {!same && <Caption1 className={styles.hint}>이전 {before}</Caption1>}
    </div>
  );
}
